import { object } from '@calendly/form/validation'
import { isEqual } from 'lodash-es'

import { determineMembers, getDurationValidationSchema } from 'components/event_type_edit'
import { isEtLocked } from 'services/event_type_locking'

import {
  formatEventTypeMembershipsAttributes,
  getAvailabilityValidationSchema,
  getFormatAvailabilityValues
} from '../components/availability'
import {
  getEventDetailsInitialValues,
  getEventDetailsLocks,
  getEventDetailsValidationSchema,
  getFormatEventDetailsValues,
  getGroupValidationSchema,
} from '../components/event_details'
import { getDoubleBookingRulesInitialValues } from '../components/free_busy_rules'
import { formatHostListMemberships, getHostListValidationSchema } from '../components/hosts'
import { formatLocationMemberships, getFormatLocationValues, getLocationValidationSchema } from '../components/location'
import { getFormatLockingRulesValues, getSectionLockInitialValues } from '../components/locking_rule_switch'
import { getNameAndColorValidationSchema } from '../components/name_and_color_fields'
import { getPaymentValidationSchema } from '../components/payment'
import { panelLockNames, PANELS } from '../constants'
import { filterEmptyValues, mergeArraysOfObjectsByKeys } from '../utils'

export const getInitialValues = ({ eventType, defaultAvailabilityRules, paymentsUpfrontVariant = null }) => {
  const eventDetailsAttributes = getEventDetailsInitialValues({
    eventType, defaultAvailabilityRules, isCreation: false, paymentsUpfrontVariant
  })

  let doubleBookingRulesAttributes = {}
  if (!eventType.isMETParent) {
    const members = determineMembers(eventType, defaultAvailabilityRules.timezone)

    doubleBookingRulesAttributes = {
      doubleBookingRulesAttributes: getDoubleBookingRulesInitialValues(Object.values(members))
    }
  }

  return ({
    ...eventDetailsAttributes,
    ...doubleBookingRulesAttributes,
    ...getSectionLockInitialValues(eventType)
  })
}

export const getEventTypeLocks = (eventType, { paymentsUpfrontVariant = null } = {}) => ({
  ...getEventDetailsLocks(eventType, { paymentsUpfrontVariant }),
  [PANELS.location]: false,
  [PANELS.availability]: isEtLocked(eventType, panelLockNames[PANELS.availability]),
})

const formatMembershipsFunctionPerPanel = {
  [PANELS.hosts]: formatHostListMemberships,
  [PANELS.availability]: formatEventTypeMembershipsAttributes,
  [PANELS.location]: formatLocationMemberships,
}

const changedMembershipsOnly = (panelKeys, initialValues, isTeam) => (membership) => {
  const initialMembershipsAttributes = mergeArraysOfObjectsByKeys(
    Object.keys(formatMembershipsFunctionPerPanel).reduce((acc, panelKey) => {
      if (panelKeys.includes(panelKey)) {
        acc.push(formatMembershipsFunctionPerPanel[panelKey](initialValues, isTeam).event_type_memberships_attributes || [])
      }

      return acc
    }, []), 'id', 'user_id', 'organization_invitation_id')

  const initialMembership = initialMembershipsAttributes.find((initial) => initial.id === membership.id)

  return !isEqual(initialMembership, membership)
}

export const getFormatValuesToServerData = ({ eventType, paymentsUpfrontVariant = null }) => (
  values, panelKeys = [], initialValues = {}
) => {
  const { isMETParent } = eventType
  const locks = isMETParent ? {} : getEventTypeLocks(eventType, { paymentsUpfrontVariant })

  const formatEventDetailsValues = getFormatEventDetailsValues({
    eventType,
    isCreation: false,
    paymentsUpfrontVariant
  })
  const formatAvailabilityValues = getFormatAvailabilityValues(eventType)
  const formatLocationValues = getFormatLocationValues(eventType)
  const formatLockingRulesValues = getFormatLockingRulesValues(eventType)

  const {
    event_type_memberships_attributes: eventDetailsMembershipsAttributes = [],
    ...eventDetailsAttributes
  } = formatEventDetailsValues(values)
  const {
    event_type_memberships_attributes: availabilityAttributesMembershipsAttributes = [],
    ...availabilityAttributes
  } = formatAvailabilityValues(values)
  const {
    event_type_memberships_attributes: locationMembershipsAttributes = [],
    ...locationAttributes
  } = formatLocationValues(values)

  const getEventTypeMembershipsAttributes = () => {
    if (isMETParent) { return {} }

    const eventTypeMembershipsAttributesToMerge = []
    if (panelKeys.includes(PANELS.hosts) && eventDetailsMembershipsAttributes) {
      eventTypeMembershipsAttributesToMerge.push(eventDetailsMembershipsAttributes)
    }
    if (panelKeys.includes(PANELS.availability) && availabilityAttributesMembershipsAttributes) {
      eventTypeMembershipsAttributesToMerge.push(availabilityAttributesMembershipsAttributes)
    }
    if (panelKeys.includes(PANELS.location) && locationMembershipsAttributes) {
      eventTypeMembershipsAttributesToMerge.push(locationMembershipsAttributes)
    }

    return {
      event_type_memberships_attributes: mergeArraysOfObjectsByKeys(
        eventTypeMembershipsAttributesToMerge,
        'id', 'user_id', 'organization_invitation_id'
      ).filter(changedMembershipsOnly(panelKeys, initialValues, eventType.isTeam))
    }
  }

  return {
    ...filterEmptyValues(eventDetailsAttributes),
    ...(!locks[PANELS.availability] ? availabilityAttributes : {}),
    ...locationAttributes,
    ...getEventTypeMembershipsAttributes(),
    ...formatLockingRulesValues(values)
  }
}

export const getValidationSchema = ({ eventType, paymentsUpfrontVariant = null }) => {
  const locks = eventType.isMETParent ? {} : getEventTypeLocks(eventType, { paymentsUpfrontVariant })

  return object({})
    .concat(getEventDetailsValidationSchema({ eventType, isCreation: false, paymentsUpfrontVariant }))
    .concat(getLocationValidationSchema(eventType))
    .concat(!locks[PANELS.availability] ? getAvailabilityValidationSchema() : object({}))
}

const getPanelSchemas = ({ eventType, paymentsUpfrontVariant = null }) => {
  const locks = eventType.isMETParent ? {} : getEventTypeLocks(eventType, { paymentsUpfrontVariant })

  return {
    [PANELS.nameAndColor]: !locks[PANELS.nameAndColor] ? getNameAndColorValidationSchema() : null,
    [PANELS.duration]: !locks[PANELS.duration] ? getDurationValidationSchema() : null,
    [PANELS.location]: getLocationValidationSchema(eventType),
    [PANELS.availability]: !locks[PANELS.availability] ? getAvailabilityValidationSchema() : null,
    [PANELS.groupEvent]: !locks[PANELS.groupEvent] ? getGroupValidationSchema(eventType) : null,
    [PANELS.hosts]: getHostListValidationSchema({ eventType }),
    ...(paymentsUpfrontVariant ? { [PANELS.payment]: getPaymentValidationSchema() } : {})
  }
}

export const getValidationSchemaForPanelKeys = ({ eventType, paymentsUpfrontVariant = null }, panelKeys) => {
  const schemas = getPanelSchemas({ eventType, paymentsUpfrontVariant })

  return panelKeys.reduce((acc, key) => {
    if (schemas[key]) return acc.concat(schemas[key])
    return acc
  }, object({}))
}

const getPanelFieldNames = (schema) => Object.keys(schema.fields)

export const getPanelFieldsMapping = ({ eventType, paymentsUpfrontVariant = null }) => Object.entries({
  [PANELS.duration]: getPanelFieldNames(getDurationValidationSchema()),
  [PANELS.location]: getPanelFieldNames(getLocationValidationSchema(eventType)),
  [PANELS.availability]: getPanelFieldNames(getAvailabilityValidationSchema()),
  [PANELS.groupEvent]: getPanelFieldNames(getGroupValidationSchema(eventType)),
  [PANELS.hosts]: getPanelFieldNames(getHostListValidationSchema({ eventType })),
  ...(paymentsUpfrontVariant ? {
    [PANELS.payment]: getPanelFieldNames(getPaymentValidationSchema())
  } : {})
})
